import { Table, Row, Col } from "react-bootstrap";
import { useCallback, useContext, useEffect, useState } from "react";
import {
  FASTING,
  OVERNIGHT,
  POST_BREAKFAST,
  POST_DINNER,
  POST_LUNCH,
  WEEKDAYS,
} from "../../util/const";
import { HourlyProps } from "../BglForm";
import { ReviewContext } from "../ReviewPatient";
import React from "react";
import { Bgl } from "../../types/observation";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import {
  FormatBglInfoDisplay,
  FormatTimeForDisplay,
  GetHourList,
  GetTimeInterval,
  Initialise,
  SetTimeslotForElement,
} from "../../util/data-utils";
import DataNavigator from "../../Components/DataNavigator";
import { Patient } from "../../types/patient";


const BglHourly = (props: HourlyProps) => {

  const context = useContext(ReviewContext);
  const patient = context.patient;

  const amLower = parseFloat(patient.amLower);
  const amUpper = parseFloat(patient.amUpper);
  const fastingLower = parseFloat(patient.fastingLower);
  const fastingUpper = parseFloat(patient.fastingUpper);

  var today = new Date(context.weekEnding);
  var timeInMillis = today.getTime();
  var day = today.getDay() + 1; // day as weekday number
  var dayCounter = 0;

  var overnightInterval = GetTimeInterval(patient.overnightEnd,patient.overnightStart);
  var fastingInterval = GetTimeInterval(patient.fastingEnd, patient.fastingStart);
  var postbreakfastInterval = GetTimeInterval(patient.breakfastEnd,patient.breakfastStart);
  var postlunchInterval = GetTimeInterval(patient.lunchEnd, patient.lunchStart);
  var postdinnerInterval = GetTimeInterval(patient.dinnerEnd, patient.dinnerStart);

  const getHourKey = (bgl: Bgl) => {
    var hour = bgl.recordedTime.split(":");
    return hour[0];
  };

  const [bglHourlyMap, setBglHourlyMap] = useState<Map<string, Map<string, Array<Bgl>>>>();

  const loadData = useCallback((aPatient: Patient) => {
    const dataMap = () => {
      var bglMap = new Map<string, Map<string, Array<Bgl>>>();
      if (aPatient.observations !== undefined && aPatient.observations.size > 0 && aPatient.observations.has("bgl")) 
      {
        var bgls = aPatient.observations.get("bgl") as Array<Bgl>;
        if (bgls !== undefined) 
        {
          Initialise(aPatient);

          bgls.forEach((element) => {
            SetTimeslotForElement(element, aPatient);
            var hourKey = getHourKey(element);
            var weekDayKey = WEEKDAYS[element.weekDay];
            if (bglMap.has(weekDayKey)) {
              var hourValues = bglMap.get(weekDayKey);
              if (hourValues?.has(hourKey)) {
                var values = hourValues.get(hourKey);
                values?.push(element);
                hourValues.set(hourKey, values!);
                bglMap.set(weekDayKey, hourValues);
              } else {
                var elements = [element];
                hourValues?.set(hourKey, elements);
                bglMap.set(weekDayKey, hourValues!);
              }
            } else {
              var newHourValues = new Map<string, Array<Bgl>>([[hourKey, [element]]]);
              // var newHourValues = new Map<string, Array<Bgl>>();
              // newHourValues.set(hourKey, [element]);
              bglMap.set(weekDayKey, newHourValues);
            }
          });
        }
      }
      return bglMap;
    }
    let bglMap = dataMap();
    setBglHourlyMap(bglMap);
  },[]);
  
  useEffect(() => {
    loadData(context.patient);
  },[context, loadData]);

  const lowBgl = (element: Bgl, display: React.JSX.Element[], tooltip: React.JSX.Element[]) => {
    display.push(<div className="low-bgl">{element.reading}</div>);
    tooltip.push(<div>{FormatTimeForDisplay(element.recordedTime)} | <span className="low-bgl">{element.reading}</span> {element.notes !== null ? " | " + element.notes : ""}</div>)
  }

  const highBgl = (element: Bgl, display: React.JSX.Element[], tooltip: React.JSX.Element[]) => {
    display.push(<div className="high-bgl">{element.reading}</div>);
    tooltip.push(<div>{FormatTimeForDisplay(element.recordedTime)} | <span className="high-bgl">{element.reading}</span> {element.notes !== null ? " | " + element.notes : ""}</div>)
  }

  const normalBgl = (element: Bgl, display: React.JSX.Element[], tooltip: React.JSX.Element[]) => {
    display.push(<div className="normal-bgl">{element.reading}</div>);
    tooltip.push(<div>{FormatTimeForDisplay(element.recordedTime)} | <span className="normal-bgl">{element.reading}</span> {element.notes !== null ? " | " + element.notes : ""}</div>)
  }

  const displayData = (bglMap: Map<string, Array<Bgl>> | undefined) => {
    let display = [];
    let hoursList = GetHourList(patient);

    if (bglMap === undefined) { /// there is no data for this day, display an empty row
      for (let i = 0; i < 24; i++) {
        let displayElement = <td key={i}></td>;
        display.push(displayElement);
      }
    } else {
      let keys = Array.from(bglMap.keys());
      let keysList = convertStringToNumber(keys);

      for (let i = 0; i < 24; i++) {
        var key = hoursList[i];
        if (keysList.indexOf(key) > -1) {
          let index = keysList.indexOf(key);
          var elements = bglMap.get(keys[index])!;
          let result = [] as React.JSX.Element[];
          let result_detail = [] as React.JSX.Element[];
          for (var element of elements) {

            if (element.postBreakfast !== undefined) {
              let postBreakfastNumber: number = parseFloat(element.postBreakfast);
              if (postBreakfastNumber < amLower) {
                lowBgl(element, result, result_detail);
              } else if (postBreakfastNumber > amUpper) {
                highBgl(element, result, result_detail);
              } else {
                normalBgl(element, result, result_detail);
              }
            }

            if (element.postLunch !== undefined) {
              let postLunchNumber: number = parseFloat(element.postLunch);
              if (postLunchNumber < amLower) {
                lowBgl(element, result, result_detail);
              } else if (postLunchNumber > amUpper) {
                highBgl(element, result, result_detail);
              } else {
                normalBgl(element, result, result_detail);
              }
            }

            if (element.postDinner !== undefined) {
              let postDinnerNumber: number = parseFloat(element.postDinner);
              if (postDinnerNumber < amLower) {
                lowBgl(element, result, result_detail);
              } else if (postDinnerNumber > amUpper) {
                highBgl(element, result, result_detail);
              } else {
                normalBgl(element, result, result_detail);
              }
            }

            if (element.overnight !== undefined) {
              let overnightNumber: number = parseFloat(element.overnight);
              if (overnightNumber < amLower) {
                lowBgl(element, result, result_detail);
              } else if (overnightNumber > amUpper) {
                highBgl(element, result, result_detail);
              } else {
                normalBgl(element, result, result_detail);
              }
            }

            if (element.fasting !== undefined) {
              let fastingNumber: number = parseFloat(element.fasting);
              if (fastingNumber < fastingLower) {
                lowBgl(element, result, result_detail);
              } else if (fastingNumber > fastingUpper) {
                highBgl(element, result, result_detail);
              } else {
                normalBgl(element, result, result_detail);
              }
            }
          }
          if (result.length > 1) {
            display.push(
              <td key={i}>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip className="tooltip">{result_detail}</Tooltip>}>
                  <span>{result[0]}...</span>
                </OverlayTrigger>
              </td>
            );
          } else {
            display.push(
              <td key={i}>
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip className="tooltip">{result_detail}</Tooltip>}>
                  <span>{result}</span>
                </OverlayTrigger>
              </td>
            );
          }
        } else { // add an empty cell
          let displayElement = <td key={i}></td>;
          display.push(displayElement);
        }
      }
    }
    return display;
  };

  const convertStringToNumber = (arrayList: Array<string>) => {
    let newList = [];
    for (let i = 0; i < arrayList.length; i++) {
      newList.push(Number(arrayList[i]));
    }
    return newList;
  };

  const getTds = (patient:Patient) => {
    var hoursList = GetHourList(patient);

    let tdList = [];
    for (let i = 0; i < hoursList.length; i++) {
      const hours = hoursList[i].toString().padStart(2, "0");
      const minutes = "00";
      tdList.push(
        <td>
          {hours}:{minutes}
        </td>
      );
    }
    return tdList;
  };

  return (
    <Table striped bordered hover className="review-table">
      <thead>
        <tr>
          <th colSpan={25}>
            <Row>
              <Col className="left">
                <DataNavigator typeKey="bgl" />
              </Col>
              <Col className="right me-2">{FormatBglInfoDisplay(patient)}</Col>
            </Row>
          </th>
        </tr>
        <tr>
          <th colSpan={25} className="section"></th>
        </tr>
        <tr>
          <th></th>
          <th colSpan={overnightInterval}>{OVERNIGHT}</th>
          <th colSpan={fastingInterval}>{FASTING}</th>
          <th colSpan={postbreakfastInterval}>{POST_BREAKFAST}</th>
          <th colSpan={postlunchInterval}>{POST_LUNCH}</th>
          <th colSpan={postdinnerInterval}>{POST_DINNER}</th>
        </tr>
        <tr>
          <th></th>
          {getTds(patient)}
        </tr>
      </thead>
      <tbody>
        { WEEKDAYS.map(() => {
          day = day - 1;
          if (day < 0) {
            day = 7 + day;
          }
          var dayKey = WEEKDAYS[day];

          var nextDate = new Date(timeInMillis - dayCounter * 24 * 3600 * 1000);
          var dayOfMonthStr = nextDate.getDate().toString();
          if(dayOfMonthStr.length === 1)
            dayOfMonthStr = "0" + dayOfMonthStr;
          dayCounter++;

          var bglMap = bglHourlyMap?.get(dayKey);
          return (
            <React.Fragment key={dayKey}>
              <tr onClick={props.toggleHourly}>
                <td>{dayKey} {dayOfMonthStr}</td>
                {displayData(bglMap)}
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};

export default BglHourly;